import { ApiError } from '../api';

/**
 * Hidden by default: the backend still registers against Grow's test system, so the entry
 * point is switched on per account for QA until production identifiers are in place.
 */
export const GROW_CONNECT_FEATURE_FLAG = 'feature-grow-connect';

/** The values double as the Formik field names on the registration form. */
export enum GrowConnectField {
  PHONE = 'phone',
  BUSINESS_NUMBER = 'business_number',
}

/**
 * - `FIELD`      the merchant can fix it in one of the two inputs.
 * - `BLOCKING`   nobody at Monkeybook can help; retrying fails the same way. Route to support.
 * - `STALE`      our view of the account may simply be out of date -- refetch and re-resolve.
 * - `RETRYABLE`  our problem, not theirs. Let them try once more.
 */
export enum RegisterErrorType {
  FIELD = 'field',
  BLOCKING = 'blocking',
  STALE = 'stale',
  RETRYABLE = 'retryable',
}

export type RegisterError = {
  message: string;
  type: RegisterErrorType;
  field?: GrowConnectField;
};

const INTERNATIONAL_DIALING_PREFIX = '00';
const ISRAELI_COUNTRY_CODE = '972';
const ISRAELI_LOCAL_MOBILE_LENGTH = 10;
const ISRAELI_MOBILE_PREFIX = '05';
const BUSINESS_NUMBER_LENGTH = 9;

// Grow refusals the merchant cannot act on: the registration already exists somewhere else in
// Grow's world, so only Grow's support can move it. Retrying just fails the same way.
const CUSTOMER_HAS_OPEN_REGISTRATION = 17;
const BLOCKING_GROW_ERROR_IDS = [
  CUSTOMER_HAS_OPEN_REGISTRATION, // opened directly with Grow, or by a different marketer
  145, // the business is not approved to join Grow
  149, // the business is already a Grow merchant
];

// Grow refusals that land on one of our two fields. Normally caught client-side first.
const GROW_ERROR_ID_TO_FIELD: Record<number, GrowConnectField> = {
  13: GrowConnectField.PHONE,
  120: GrowConnectField.BUSINESS_NUMBER,
};

/**
 * Mirrors utils/phone_utils.to_israeli_local_mobile on the backend so the merchant gets a fast,
 * kind error instead of a round-trip. Returns undefined for anything that is not an Israeli
 * mobile -- landlines and foreign numbers included.
 */
export const normalizeIsraeliMobile = (phone: string | undefined): string | undefined => {
  let digits = (phone || '').replace(/\D/g, '');

  if (digits.startsWith(INTERNATIONAL_DIALING_PREFIX + ISRAELI_COUNTRY_CODE)) {
    digits = digits.slice((INTERNATIONAL_DIALING_PREFIX + ISRAELI_COUNTRY_CODE).length);
  } else if (digits.startsWith(ISRAELI_COUNTRY_CODE)) {
    digits = digits.slice(ISRAELI_COUNTRY_CODE.length);
  }

  const localPhone = `0${digits.replace(/^0+/, '')}`;
  if (localPhone.length !== ISRAELI_LOCAL_MOBILE_LENGTH || !localPhone.startsWith(ISRAELI_MOBILE_PREFIX)) {
    return undefined;
  }
  return localPhone;
};

/**
 * Mirrors GrowRegistrationLogic._normalize_business_number. Short numbers are padded rather than
 * rejected, so an eight digit exempt dealer number is fine. We submit the padded value so that
 * what the merchant sees on the pending screen is what they sent.
 */
export const normalizeBusinessNumber = (businessNumber: string | undefined): string | undefined => {
  const digits = (businessNumber || '').replace(/\D/g, '');
  if (!digits || digits.length > BUSINESS_NUMBER_LENGTH) {
    return undefined;
  }
  return digits.padStart(BUSINESS_NUMBER_LENGTH, '0');
};

/**
 * Register raises FastAPI HTTPExceptions, so the body is `{detail: ...}`, never `{message: ...}`.
 *
 * Merchant facing failures carry an object with a Hebrew `message` written for the merchant to
 * read verbatim. The non-Israeli-account and Grow-unreachable cases historically carried a bare
 * English developer string instead; we never render those, even though ExtraPaymentDialog does
 * render string details elsewhere -- these particular strings were written for our logs.
 *
 * Only the mapped Grow refusals carry `grow_error_id`. Every other 400 (already connected, no
 * storefront, bad phone, bad business number) arrives as a bare `{message}`, which is why those
 * are classified `stale` and trigger a refetch rather than being read as a permanent failure.
 */
export const classifyRegisterError = (
  error: unknown,
  t: (key: string) => string,
  options?: { isResendForSameBusinessNumber?: boolean },
): RegisterError => {
  if (!(error instanceof ApiError)) {
    return { message: t('growConnect.errors.generic'), type: RegisterErrorType.RETRYABLE };
  }

  const detail = error.body?.detail;
  const isMerchantFacing = !!detail && typeof detail === 'object';

  // Grow passes err.id through unmodified, so it can arrive as a string.
  const rawGrowErrorId = isMerchantFacing ? Number(detail.grow_error_id) : NaN;
  const growErrorId = Number.isFinite(rawGrowErrorId) ? rawGrowErrorId : undefined;

  // 403 is the one case we always phrase ourselves. Its server text is English either way, and
  // the only accounts that can trigger it are non-Israeli ones whose admin may be reading in
  // either language -- our own key at least follows the UI language.
  if (error.status === 403) {
    return { message: t('growConnect.errors.notIsraeliAccount'), type: RegisterErrorType.BLOCKING };
  }

  const message: string = isMerchantFacing && detail.message ? detail.message : t('growConnect.errors.generic');

  // Resending to a corrected mobile re-registers the same business number. If Grow reports the
  // registration as already open, that is our own lead from a minute ago -- telling them to call
  // Grow's support would be nonsense. Point them at the link they already have.
  if (growErrorId === CUSTOMER_HAS_OPEN_REGISTRATION && options?.isResendForSameBusinessNumber) {
    return { message: t('growConnect.errors.leadAlreadyOpen'), type: RegisterErrorType.RETRYABLE };
  }

  if (growErrorId !== undefined && GROW_ERROR_ID_TO_FIELD[growErrorId]) {
    return { message, type: RegisterErrorType.FIELD, field: GROW_ERROR_ID_TO_FIELD[growErrorId] };
  }

  // 409 is a business number claimed by another Monkeybook account. Only we can untangle it.
  if (error.status === 409) {
    return { message, type: RegisterErrorType.BLOCKING };
  }

  if (growErrorId !== undefined) {
    return {
      message,
      type: BLOCKING_GROW_ERROR_IDS.includes(growErrorId) ? RegisterErrorType.BLOCKING : RegisterErrorType.RETRYABLE,
    };
  }

  if (error.status === 400) {
    return { message, type: RegisterErrorType.STALE };
  }

  return { message, type: RegisterErrorType.RETRYABLE };
};
